import React from "react";
import { api } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { TextInput, toast } from "@/components/admin/ui";

/* =========================================================================
   2High · Owner Cabinet — Email Marketing List
   -------------------------------------------------------------------------
   List of every email captured via the Reward Wheel popup.
   Operator can:
     - Search / filter
     - Mark a spin "claimed" once the prize was redeemed
     - Reset a single spin so that email can spin again
     - Reset the whole campaign (everyone can spin once more)
     - Export the list as CSV for use in any email tool (Mailchimp/Resend/etc.)
   ========================================================================= */

const PRIZE_LABEL = {
  "5off": "5% Off",
  preroll: "Free Pre-roll",
  sticker: "Sticker Pack",
  jar: "Free Jar Upgrade",
  mystery: "Mystery Bonus",
};

export default function Marketing() {
  const [spins, setSpins] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all"); // all | unclaimed | claimed

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listWheelSpins();
      setSpins(list);
    } catch (e) {
      toast("Failed to load marketing list", "error");
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return spins.filter((s) => {
      if (q && !(s.email || "").toLowerCase().includes(q)) return false;
      if (filter === "unclaimed" && s.claimed_at) return false;
      if (filter === "claimed" && !s.claimed_at) return false;
      return true;
    });
  }, [spins, search, filter]);

  const claimedCount = spins.filter(s => s.claimed_at).length;
  const todayCount = spins.filter(s => {
    const d = (s.spun_at || "").slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return d === today;
  }).length;

  async function resetOne(s) {
    if (!window.confirm(`Reset spin for ${s.email}?\nThey'll be able to spin again with this email.`)) return;
    try {
      await api.deleteWheelSpin(s.id);
      toast("Spin reset · they can spin again", "ok");
      reload();
    } catch { toast("Reset failed", "error"); }
  }

  async function markClaimed(s) {
    try {
      await api.markWheelClaimed(s.id);
      toast(`Marked ${s.email} as claimed`, "ok");
      reload();
    } catch { toast("Failed to mark claimed", "error"); }
  }

  async function resetCampaign() {
    if (!window.confirm("Reset the entire campaign?\n\nEvery email in the list will be eligible to spin one more time on a fresh campaign. The current list stays in place for reference.")) return;
    try {
      const r = await api.resetWheelCampaign();
      toast(`Fresh campaign started · ${r.campaign_id}`, "ok");
      reload();
    } catch { toast("Failed to reset campaign", "error"); }
  }

  function exportCsv() {
    const rows = [["email", "prize", "spun_at", "claimed_at", "ip"]];
    filtered.forEach(s => {
      rows.push([
        s.email,
        PRIZE_LABEL[s.prize_id] || s.prize_id,
        s.spun_at || "",
        s.claimed_at || "",
        s.ip || "",
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `2high-email-list-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast(`Exported ${filtered.length} emails`, "ok");
  }

  return (
    <div data-testid="marketing-page" className="space-y-5">
      <PageHead
        eye="Members · Email capture from the reward wheel"
        title="Email List"
        sub="Every email captured via the public spin-the-wheel popup. Build campaigns. Reset spins. Export for Mailchimp/Resend/etc."
        right={
          <div className="flex items-center gap-2">
            <button
              data-testid="mkt-export"
              onClick={exportCsv}
              disabled={!filtered.length}
              className="px-3 py-1.5 rounded-md border border-[#2c3122] text-[11px] tracking-[.18em] uppercase font-mono text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] disabled:opacity-30"
            >Export CSV</button>
            <button
              data-testid="mkt-reset-campaign"
              onClick={resetCampaign}
              className="px-3 py-1.5 rounded-md border border-[#c9a86b]/40 text-[11px] tracking-[.18em] uppercase font-mono text-[#c9a86b] hover:bg-[#c9a86b]/10"
            >Reset Campaign</button>
          </div>
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
        <StatCard label="Total Emails" value={spins.length} testid="mkt-stat-total" />
        <StatCard label="Today" value={todayCount} testid="mkt-stat-today" accent="#a8d39d" />
        <StatCard label="Claimed" value={claimedCount} testid="mkt-stat-claimed" accent="#c9a86b" />
        <StatCard label="Pending" value={spins.length - claimedCount} testid="mkt-stat-pending" accent="#7fbfff" />
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-2 max-w-4xl">
        <TextInput
          data-testid="mkt-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email…"
          className="flex-1"
        />
        <div className="flex gap-1">
          {["all", "unclaimed", "claimed"].map((k) => (
            <button
              key={k}
              data-testid={`mkt-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-md text-[11px] tracking-[.18em] uppercase font-mono border ${filter === k ? "bg-[#c9a86b]/15 border-[#c9a86b] text-[#e2c790]" : "border-[#2c3122] text-[#a9aa9a]"}`}
            >{k}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden max-w-4xl">
        <div className="hidden md:grid grid-cols-[1.6fr_1fr_1fr_1fr_120px] gap-3 px-4 py-3 text-[10px] tracking-[.22em] uppercase text-[#6e6f5f] bg-[#10120c] border-b border-[#22261b]">
          <div>Email</div>
          <div>Prize</div>
          <div>Spun</div>
          <div>Claimed</div>
          <div className="text-right">Actions</div>
        </div>
        {loading && <div className="text-[#6e6f5f] text-[13px] p-6 text-center">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-[#6e6f5f] text-[13px] p-6 text-center">
            {spins.length === 0
              ? "No spins yet. When visitors enter their email and spin the wheel, they'll appear here."
              : "No matches for the current filter."}
          </div>
        )}
        {!loading && filtered.map((s) => (
          <div
            key={s.id}
            data-testid={`mkt-row-${s.id}`}
            className="grid grid-cols-[1.6fr_1fr_1fr_1fr_120px] gap-3 items-center px-4 py-3 border-b border-[#22261b] last:border-b-0 text-[12.5px]"
          >
            <div className="font-mono text-[#e8e4d8] truncate" title={s.email}>{s.email}</div>
            <div className="text-[#a8d39d] text-[11px] tracking-[.1em]">{PRIZE_LABEL[s.prize_id] || s.prize_label}</div>
            <div className="text-[#6e6f5f] text-[11px] font-mono">{formatDate(s.spun_at)}</div>
            <div className="text-[11px] font-mono">
              {s.claimed_at
                ? <span className="text-[#5fa15a]">✓ {formatDate(s.claimed_at)}</span>
                : <span className="text-[#6e6f5f]">—</span>}
            </div>
            <div className="flex justify-end gap-1">
              {!s.claimed_at && (
                <button
                  data-testid={`mkt-claim-${s.id}`}
                  onClick={() => markClaimed(s)}
                  title="Mark prize as claimed"
                  className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a8d39d] hover:border-[#5fa15a] hover:bg-[#5fa15a]/10"
                >✓</button>
              )}
              <button
                data-testid={`mkt-reset-${s.id}`}
                onClick={() => resetOne(s)}
                title="Reset this spin (let them spin again)"
                className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#c9a86b] hover:border-[#c9a86b] hover:bg-[#c9a86b]/10"
              >↻</button>
            </div>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-[#6e6f5f] leading-relaxed max-w-4xl">
        <strong className="text-[#a9aa9a]">Tip:</strong> The wheel can be enabled/disabled from <a href="/owner-cabinet/settings" className="text-[#c9a86b] hover:underline">Settings</a>.
        Use Reset Campaign before a new drop so every member gets one fresh spin. Export the CSV to send announcements via Resend, Mailchimp, or any email tool.
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = "#e8e4d8", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-[#22261b] bg-[#13150f] p-4">
      <div className="text-[10.5px] tracking-[.2em] uppercase text-[#6e6f5f] font-mono">{label}</div>
      <div className="text-[28px] font-bold leading-none mt-2 tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      + " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return iso.slice(0, 10); }
}
